import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface AppSelectOption {
  value: string;
  label: string;
  /** Optional compact mark on the right (e.g. „Standard“). */
  badge?: string;
  /** Optional leading mark (e.g. category color dot). */
  icon?: React.ReactNode;
}

interface AppSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: AppSelectOption[];
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  /** Optional leading icon inside the trigger (e.g. context for the field). */
  icon?: React.ReactNode;
  id?: string;
  size?: 'sm' | 'md';
  /** `ghost` drops the frame for toolbars — hover carries the affordance instead. */
  variant?: 'default' | 'ghost';
  'aria-label'?: string;
}

/** Gleiche vertikale Maße wie Standard-Textfelder (z. B. Materialien-Formular): py-2, text-sm */
const TRIGGER_BASE =
  'w-full min-h-[2.5rem] min-w-0 box-border flex items-center justify-between gap-2 text-left font-normal text-app-text rounded-lg px-3 py-2 text-sm leading-normal outline-none disabled:opacity-60 disabled:cursor-not-allowed';

const TRIGGER_VARIANT: Record<'default' | 'ghost', string> = {
  default:
    'bg-app-surface border border-app-border focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue',
  // Only `focus-visible` so a mouse click stays clean while keyboard focus remains visible.
  ghost:
    'border-0 bg-transparent !font-medium !text-app-text-muted hover:bg-app-hover hover:!text-app-text focus-visible:ring-2 focus-visible:ring-pipedrive-blue/20',
};

/**
 * Styled dropdown that always uses our app design (no native select).
 */
export function AppSelect({
  value,
  onChange,
  options,
  disabled = false,
  className = '',
  triggerClassName = '',
  icon = null,
  id,
  size = 'md',
  variant = 'default',
  'aria-label': ariaLabel,
}: AppSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const selectedIndex = React.useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value]
  );
  const displayLabel = selected?.label ?? value;

  React.useEffect(() => {
    if (!open) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [open, selectedIndex]);

  React.useEffect(() => {
    if (!open || highlightedIndex < 0) return;
    optionRefs.current[highlightedIndex]?.focus();
  }, [open, highlightedIndex]);

  const commitSelection = React.useCallback(
    (nextValue: string) => {
      onChange(nextValue);
      setOpen(false);
    },
    [onChange]
  );

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex(
        e.key === 'ArrowDown'
          ? selectedIndex >= 0
            ? selectedIndex
            : 0
          : Math.max(options.length - 1, 0)
      );
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((prev) => !prev);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((index + 1) % options.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((index - 1 + options.length) % options.length);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      setHighlightedIndex(0);
      return;
    }
    if (e.key === 'End') {
      e.preventDefault();
      setHighlightedIndex(options.length - 1);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      commitSelection(options[index].value);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const triggerClass = `${TRIGGER_BASE} ${TRIGGER_VARIANT[variant]}`;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        className={`${triggerClass} ${triggerClassName}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="min-w-0 flex-1 flex items-center gap-1.5">
          {icon ? <span className="inline-flex shrink-0 items-center text-app-text-subtle">{icon}</span> : null}
          {selected?.icon ? <span className="inline-flex shrink-0 items-center">{selected.icon}</span> : null}
          <span className="truncate">{displayLabel}</span>
          {selected?.badge ? (
            <span className="shrink-0 rounded bg-app-surface-muted px-1.5 py-0.5 text-[10px] font-medium text-app-text-muted">
              {selected.badge}
            </span>
          ) : null}
        </span>
        <ChevronDown className="w-4 h-4 shrink-0 text-app-text-subtle" />
      </button>
      {open && (
        <div
          data-app-select-panel
          className="absolute top-full left-0 right-0 z-50 mt-1 py-1 bg-app-surface border border-app-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
          role="listbox"
        >
          {options.map((opt, index) => (
            <button
              key={opt.value}
              type="button"
              ref={(node) => {
                optionRefs.current[index] = node;
              }}
              onClick={() => {
                commitSelection(opt.value);
              }}
              onKeyDown={(e) => handleOptionKeyDown(e, index)}
              className={`w-full flex items-center gap-2 text-left px-3 transition-colors ${
                size === 'sm' ? 'py-1.5 text-xs' : 'py-2 text-sm'
              } ${
                index === highlightedIndex
                  ? 'bg-pipedrive-blue text-white'
                  : 'text-app-text hover:bg-app-hover'
              }`}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.icon ? <span className="inline-flex shrink-0 items-center">{opt.icon}</span> : null}
              <span className="truncate min-w-0 flex-1">{opt.label}</span>
              {opt.badge ? (
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    index === highlightedIndex
                      ? 'bg-white/20 text-white'
                      : 'bg-app-surface-muted text-app-text-muted'
                  }`}
                >
                  {opt.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
